import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
// PageFlip
import HTMLFlipBook from 'react-pageflip';

import { showStats } from 'actions/gameActions';
import { GO_BACK, HELP, SPEAK, GO, STOP, PARENT_ADVICE } from 'consts/buttons';
import { setActivityMastered } from 'actions/flinkPlayActions';
import { toggleAudio, stopAudio } from 'actions/audioActions';
import { activitiesURL } from 'config';
import { validateProblem } from './ebookHelpers';
import {
  AdaptiveContainer,
  ActivityButtons,
  EbookComponent,
  EbookTimer,
} from 'components/flink-play';
import { PRESENTATION, MANUAL, BOTH } from 'consts/ebook-consts';

const NO_MEDIA_DELAY = 5 * 1000;
const WITH_MEDIA_DELAY = 2 * 1000;
const FLIP_TIME = 850;

const BOOK_WIDTH = 1020;
const BOOK_HEIGHT = 680;

class Ebook extends Component {
  state = {
    isFlipping: false,
    flipKey: 0,
    bookW: BOOK_WIDTH,
    bookH: BOOK_HEIGHT,
  };

  _isDispatchingSynthetic = false;

  _dragCaptureCancelOnEnd = false;

  _dragCaptureFlipOnEnd = false;
  _dragCaptureFlipCorner = null; // 'top' | 'bottom'

  _dragCaptureForcedInstant = false;

  _dragCaptureStartPt = null;

  flipRef = React.createRef();

  RESIZE_THROTTLE_MS = 240;
  RESIZE_MIN_DELTA_PX = 10;
  _resizeTO = null;
  _pendingResize = null;

  _dragCaptureBound = false;
  _dragCaptureActive = false;
  _dragCaptureRoot = null;
  _dragCaptureStart = null;
  _dragCaptureMove = null;
  _dragCaptureEnd = null;
  _dragCapturePrevUserSelect = null;

  bindDragCaptureFix = (reason = 'unknown') => {
    const root = this.getFlipRoot();
    if (!root) {
      console.log('[Ebook][DragCapture] bind skip (no root)', { reason });
      return;
    }

    if (this._dragCaptureBound && this._dragCaptureRoot === root) return;

    this.unbindDragCaptureFix(`rebind:${reason}`);

    this._dragCaptureRoot = root;

    const ZONE_SIZE = 140;
    const RIGHT_EDGE_WIDTH = 150;

    const CANCEL_OVERSHOOT_PX = 6;

    const MIN_FOLD_DIST_PX = 0;

    const dist = (a, b) => {
      if (!a || !b) return 0;
      const dx = (b.x - a.x);
      const dy = (b.y - a.y);
      return Math.sqrt(dx * dx + dy * dy);
    };

    const getCornerZone = (rect, corner) => {
      if (corner === 'top') {
        return {
          left: rect.right - ZONE_SIZE,
          right: rect.right,
          top: rect.top,
          bottom: rect.top + ZONE_SIZE,
        };
      }
      // bottom
      return {
        left: rect.right - ZONE_SIZE,
        right: rect.right,
        top: rect.bottom - ZONE_SIZE,
        bottom: rect.bottom,
      };
    };

    const inRect = (rect, x, y) =>
      x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

    const inZone = (z, x, y) =>
      x >= z.left && x <= z.right && y >= z.top && y <= z.bottom;

    const forceFlipNextNow = (why = 'unknown', corner = 'bottom') => {
      if (this._dragCaptureForcedInstant) return;

      const r = this._dragCaptureRoot;
      if (!r) return;

      this._dragCaptureForcedInstant = true;

      try {
        document.removeEventListener('mousemove', this._dragCaptureMove, true);
        document.removeEventListener('mouseup', this._dragCaptureEnd, true);
      } catch (e) {}

      if (typeof this._dragCapturePrevUserSelect === 'string') {
        document.body.style.userSelect = this._dragCapturePrevUserSelect;
      }

      const rect = r.getBoundingClientRect();
      const cx = rect.left + rect.width * 0.55;
      const cy = rect.top + rect.height * 0.55;

      this._isDispatchingSynthetic = true;
      try {
        r.dispatchEvent(
          new MouseEvent('mouseup', {
            bubbles: true,
            cancelable: true,
            clientX: cx,
            clientY: cy,
            view: window,
            buttons: 0,
          })
        );
      } catch (e) {}
      this._isDispatchingSynthetic = false;

      this._dragCaptureActive = false;

      try {
        const pf = this.flipRef.current?.pageFlip?.();
        const total = (this.state.pages && this.state.pages.length) ? this.state.pages.length : 0;

        let idx = this.state.currentPageIdx || 0;
        if (pf && typeof pf.getCurrentPageIndex === 'function') {
          try {
            const real = pf.getCurrentPageIndex();
            if (typeof real === 'number' && !Number.isNaN(real)) idx = real;
          } catch (e) {}
        }

        const canFlipNext = total > 1 && idx < (total - 1);

        console.log('[Ebook][DragCapture] FORCE flipNext NOW', {
          why,
          corner,
          idx,
          total,
          canFlipNext,
        });

        if (pf && typeof pf.flipNext === 'function' && canFlipNext) {
          let ret;
          try {
            ret = pf.flipNext(corner);
          } catch (e) {
            try {
              ret = pf.flipNext();
            } catch (e2) {}
          }
          console.log('[Ebook][DragCapture] FORCE flipNext result', { ret, corner, why });
        } else {
          console.log('[Ebook][DragCapture] FORCE flipNext skipped', {
            why,
            hasPf: !!pf,
            canFlipNext,
          });
        }
      } catch (err) {
        console.warn('[Ebook][DragCapture] FORCE flipNext failed', err);
      }

      this._dragCaptureCancelOnEnd = false;
      this._dragCaptureFlipOnEnd = false;
      this._dragCaptureFlipCorner = null;
      this._dragCaptureStartPt = null;

      console.log('[Ebook][DragCapture] forceFlipNextNow done', { why });
    };

    this._dragCaptureStart = (e) => {
      if (!e || e.button !== 0) return;
      if (this._dragCaptureActive) return;

      this._dragCaptureCancelOnEnd = false;
      this._dragCaptureFlipOnEnd = false;
      this._dragCaptureFlipCorner = null;
      this._dragCaptureForcedInstant = false;
      this._dragCaptureStartPt = null;

      const t = e && e.target;
      if (
        t &&
        t.closest &&
        t.closest('button, a, input, textarea, select, [role="button"], .globa-button-pagefliparea')
      ) {
        console.log('[Ebook][DragCapture] start skip (ui)', { tag: t.tagName, cls: t.className });
        return;
      }

      const rect = root.getBoundingClientRect();
      const isRightEdge = e.clientX > rect.right - RIGHT_EDGE_WIDTH;

      const isInTopZone = e.clientY < rect.top + ZONE_SIZE;
      const isInBottomZone = e.clientY > rect.bottom - ZONE_SIZE;

      let corner = null;
      if (isInTopZone && isInBottomZone) {
        corner = ((e.clientY - rect.top) <= (rect.bottom - e.clientY)) ? 'top' : 'bottom';
      } else if (isInTopZone) {
        corner = 'top';
      } else if (isInBottomZone) {
        corner = 'bottom';
      }

      console.log('[Ebook][ClickDebug]', {
        clientX: e.clientX,
        rectRight: rect.right,
        distanceFromRight: rect.right - e.clientX,
        isRightEdge,
        clientY: e.clientY,
        topZone: rect.top + ZONE_SIZE,
        bottomZone: rect.bottom - ZONE_SIZE,
        isInTopZone,
        isInBottomZone,
        overlapZones: (isInTopZone && isInBottomZone),
        chosenCorner: corner,
      });

      if (!isRightEdge) return;

      if (!isInTopZone && !isInBottomZone) {
        console.log('[Ebook][QuickFlip] immediate flipNext — click OUTSIDE the corner zones, on the right edge');

        const pf = this.flipRef.current?.pageFlip?.();
        if (pf && typeof pf.flipNext === 'function') {
          pf.flipNext();
        } else {
          console.log('[Ebook][QuickFlip] fallback: setState +1');
          this.setState((prev) => ({
            currentPageIdx: Math.min(prev.currentPageIdx + 1, (this.state.pages?.length || 1) - 1),
          }));
        }

        e.preventDefault();
        e.stopPropagation();
        return;
      }

      this._dragCaptureFlipCorner = corner || (isInTopZone ? 'top' : 'bottom');
      this._dragCaptureStartPt = { x: e.clientX, y: e.clientY };

      this._dragCaptureActive = true;

      this._dragCapturePrevUserSelect = document.body.style.userSelect;
      document.body.style.userSelect = 'none';

      document.addEventListener('mousemove', this._dragCaptureMove, true);
      document.addEventListener('mouseup', this._dragCaptureEnd, true);

      console.log('[Ebook][DragCapture] drag start (inside corner zone)', {
        corner: this._dragCaptureFlipCorner,
      });
    };

    this._dragCaptureMove = (e) => {
      if (!this._dragCaptureActive) return;

      const r = this._dragCaptureRoot;
      if (!r) return;

      const rect = r.getBoundingClientRect();

      const corner = this._dragCaptureFlipCorner === 'top' ? 'top' : 'bottom';

      const cx = e.clientX;
      const cy = e.clientY;

      const zone = getCornerZone(rect, corner);
      const moved = dist(this._dragCaptureStartPt, { x: cx, y: cy });
      const insideCornerZone = inZone(zone, cx, cy);

      if (!insideCornerZone && moved >= MIN_FOLD_DIST_PX) {
        console.log('[Ebook][DragCapture] leave-corner-zone -> FORCE NOW', {
          corner,
          moved: Math.round(moved),
          start: this._dragCaptureStartPt,
          pos: { x: cx, y: cy },
          zone,
        });
        try { e.preventDefault(); e.stopPropagation(); } catch (err) {}
        forceFlipNextNow('leave-corner-zone', corner);
        return;
      }

      const overshootDown = cy > rect.bottom + CANCEL_OVERSHOOT_PX;
      const overshootUp = cy < rect.top - CANCEL_OVERSHOOT_PX;

      const shouldForceNow =
        (corner === 'bottom' && overshootDown) ||
        (corner === 'top' && overshootUp);

      if (shouldForceNow) {
        console.log('[Ebook][DragCapture] overshoot -> FORCE NOW', {
          corner,
          clientY: cy,
          top: rect.top,
          bottom: rect.bottom,
          overshootDown,
          overshootUp,
        });
        try { e.preventDefault(); e.stopPropagation(); } catch (err) {}
        forceFlipNextNow('overshoot-move', corner);
        return;
      }

      if ((overshootDown || overshootUp) && !this._dragCaptureFlipOnEnd) {
        this._dragCaptureFlipOnEnd = true;
        console.log('[Ebook][DragCapture] overshoot -> flipOnEnd', {
          clientY: cy,
          top: rect.top,
          bottom: rect.bottom,
          overshootDown,
          overshootUp,
          corner,
        });
      }

      const insideBookRect = inRect(rect, cx, cy);
      if (insideBookRect) return;

      let correctedX = cx;
      let correctedY = cy;

      if (cy > rect.bottom + 80) correctedY = rect.bottom - 20;
      if (cy < rect.top - 80) correctedY = rect.top + 20;

      if (cx > rect.right + 80) correctedX = rect.right - 20;
      if (cx < rect.left - 80) correctedX = rect.left + 20;

      r.dispatchEvent(
        new MouseEvent('mousemove', {
          bubbles: true,
          cancelable: true,
          clientX: correctedX,
          clientY: correctedY,
          view: window,
          buttons: 1,
          button: 0,
        })
      );
    };

    this._dragCaptureEnd = (e) => {
      if (!this._dragCaptureActive || this._isDispatchingSynthetic) return;

      if (this._dragCaptureForcedInstant) {
        this._dragCaptureActive = false;

        document.removeEventListener('mousemove', this._dragCaptureMove, true);
        document.removeEventListener('mouseup', this._dragCaptureEnd, true);

        if (typeof this._dragCapturePrevUserSelect === 'string') {
          document.body.style.userSelect = this._dragCapturePrevUserSelect;
        }

        this._dragCaptureCancelOnEnd = false;
        this._dragCaptureFlipOnEnd = false;
        this._dragCaptureFlipCorner = null;
        this._dragCaptureStartPt = null;

        console.log('[Ebook][DragCapture] drag end (forcedInstant)');
        return;
      }

      const r = this._dragCaptureRoot;

      this._isDispatchingSynthetic = true;

      try {
        if (r) {
          const rect = r.getBoundingClientRect();

          let clientX = e?.clientX ?? rect.right - 20;
          let clientY = e?.clientY ?? rect.bottom - 20;

          if (clientY > rect.bottom + 80) clientY = rect.bottom - 20;
          if (clientY < rect.top - 80) clientY = rect.top + 20;

          if (clientX > rect.right + 80) clientX = rect.right - 20;
          if (clientX < rect.left - 80) clientX = rect.left + 20;

          r.dispatchEvent(
            new MouseEvent('mouseup', {
              bubbles: true,
              cancelable: true,
              clientX,
              clientY,
              view: window,
              buttons: 0,
            })
          );

          if (this._dragCaptureFlipOnEnd) {
            const pf = this.flipRef.current?.pageFlip?.();
            const beforeIdx =
              (pf && typeof pf.getCurrentPageIndex === 'function')
                ? pf.getCurrentPageIndex()
                : this.state.currentPageIdx;

            const corner = this._dragCaptureFlipCorner === 'top' ? 'top' : 'bottom';

            console.log('[Ebook][DragCapture] flipOnEnd -> post-check scheduled', {
              beforeIdx,
              corner,
              stateIdx: this.state.currentPageIdx,
              pages: this.state.pages ? this.state.pages.length : null,
            });

            setTimeout(() => {
              try {
                const pf2 = this.flipRef.current?.pageFlip?.();
                if (!pf2 || typeof pf2.getCurrentPageIndex !== 'function') {
                  console.log('[Ebook][DragCapture] flipOnEnd -> no pageflip instance');
                  return;
                }

                const afterIdx = pf2.getCurrentPageIndex();
                const total = (this.state.pages && this.state.pages.length) ? this.state.pages.length : 0;
                const canFlipNext = total ? afterIdx < (total - 1) : true;

                console.log('[Ebook][DragCapture] flipOnEnd -> post-check', {
                  beforeIdx,
                  afterIdx,
                  total,
                  canFlipNext,
                  corner,
                });

                if (afterIdx === beforeIdx && canFlipNext && typeof pf2.flipNext === 'function') {
                  let ret;
                  try {
                    ret = pf2.flipNext(corner);
                  } catch (err) {
                    try {
                      ret = pf2.flipNext();
                    } catch (e2) {}
                  }
                  console.log('[Ebook][DragCapture] flipOnEnd -> flipNext forced', { ret, corner });
                }
              } catch (err) {
                console.warn('[Ebook][DragCapture] flipOnEnd post-check failed', err);
              }
            }, 0);
          }
        }
      } catch (err) {
        console.warn('[Ebook][DragCapture] mouseup dispatch failed', err);
      }

      this._isDispatchingSynthetic = false;

      this._dragCaptureActive = false;

      document.removeEventListener('mousemove', this._dragCaptureMove, true);
      document.removeEventListener('mouseup', this._dragCaptureEnd, true);

      if (typeof this._dragCapturePrevUserSelect === 'string') {
        document.body.style.userSelect = this._dragCapturePrevUserSelect;
      }

      this._dragCaptureCancelOnEnd = false;
      this._dragCaptureFlipOnEnd = false;
      this._dragCaptureFlipCorner = null;
      this._dragCaptureForcedInstant = false;
      this._dragCaptureStartPt = null;

      console.log('[Ebook][DragCapture] drag end');
    };

    root.addEventListener('mousedown', this._dragCaptureStart, true);

    this._dragCaptureBound = true;

    console.log('[Ebook][DragCapture] bound', { reason, rootClass: root.className });
  };

  unbindDragCaptureFix = (reason = 'unknown') => {
    if (this._dragCaptureActive) {
      this._dragCaptureActive = false;
      document.removeEventListener('mousemove', this._dragCaptureMove, true);
      document.removeEventListener('mouseup', this._dragCaptureEnd, true);

      if (typeof this._dragCapturePrevUserSelect === 'string') {
        document.body.style.userSelect = this._dragCapturePrevUserSelect;
      }
    }

    if (this._dragCaptureRoot && this._dragCaptureStart) {
      try {
        this._dragCaptureRoot.removeEventListener('mousedown', this._dragCaptureStart, true);
      } catch (e) {}
    }

    this._dragCaptureBound = false;
    this._dragCaptureRoot = null;
    this._dragCaptureStart = null;
    this._dragCaptureMove = null;
    this._dragCaptureEnd = null;

    this._dragCaptureCancelOnEnd = false;
    this._dragCaptureFlipOnEnd = false;
    this._dragCaptureFlipCorner = null;
    this._dragCaptureForcedInstant = false;
    this._dragCaptureStartPt = null;

    console.log('[Ebook][DragCapture] unbound', { reason });
  };

  getComputedBookSize = () => {
    const screen =
      document.querySelector('.FlinkPlay_content__3rdm') ||
      document.querySelector('.FlinkPlay_content__3rdrm') ||
      document.querySelector('[class*="FlinkPlay_content__"]') ||
      document.querySelector('.pageflip-wrapper');

    const outer = document.querySelector('.ebookFlipBookOuter');

    if (!screen || !outer) {
      return { ok: false, reason: 'no-screen-or-outer', screen: !!screen, outer: !!outer };
    }

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const panel = document.querySelector('.Buttons_outerClass__3NwhM');
    const panelH = panel ? Math.ceil(panel.getBoundingClientRect().height) : 96;

    const timerEl =
      (screen && screen.querySelector
        ? (screen.querySelector('[class*="EbookTimer"]') || screen.querySelector('[class*="Timer"]'))
        : null) ||
      document.querySelector('.pageflip-wrapper [class*="EbookTimer"]') ||
      document.querySelector('.pageflip-wrapper [class*="Timer"]');

    const timerH = timerEl ? Math.ceil(timerEl.getBoundingClientRect().height) : 0;

    const safeTop = timerH ? (timerH + 28) : 24;
    const safeBottom = panelH + 24;

    screen.style.setProperty('--ebookSafeTop', `${safeTop}px`);
    screen.style.setProperty('--ebookSafeBottom', `${safeBottom}px`);
    outer.style.setProperty('--ebookSafeTop', `${safeTop}px`);
    outer.style.setProperty('--ebookSafeBottom', `${safeBottom}px`);

    const ocs = window.getComputedStyle(outer);
    const padL = parseFloat(ocs.paddingLeft) || 0;
    const padR = parseFloat(ocs.paddingRight) || 0;
    const padX = padL + padR;

    const availW = Math.max(0, vw - padX);
    const availH = Math.max(0, vh - safeTop - safeBottom);

    const scaleW = availW / BOOK_WIDTH;
    const scaleH = availH / BOOK_HEIGHT;

    const scaleRawUnclamped = Math.max(0.1, Math.min(scaleW, scaleH));
    const scaleRaw = Math.max(0.1, Math.min(1, scaleRawUnclamped));

    const toEven = (n) => (n % 2 ? n - 1 : n);

    let bookW = Math.max(2, Math.floor(BOOK_WIDTH * scaleRaw));
    bookW = Math.max(2, toEven(bookW));
    let scale = bookW / BOOK_WIDTH;

    let bookH = Math.max(2, Math.floor(BOOK_HEIGHT * scale));
    bookH = Math.max(2, toEven(bookH));

    if (bookH > availH) {
      bookH = Math.max(2, toEven(Math.floor(availH)));
      scale = bookH / BOOK_HEIGHT;
      bookW = Math.max(2, toEven(Math.floor(BOOK_WIDTH * scale)));
    }
    if (bookW > availW) {
      bookW = Math.max(2, toEven(Math.floor(availW)));
      scale = bookW / BOOK_WIDTH;
      bookH = Math.max(2, toEven(Math.floor(BOOK_HEIGHT * scale)));
    }

    outer.style.setProperty('--ebookScale', String(scale));

    console.log('[Ebook][Scale][Avail]', {
      vw,
      vh,
      safeTop,
      safeBottom,
      padL,
      padR,
      padX,
      availW,
      availH,
      scaleW,
      scaleH,
      scaleRawUnclamped,
      scaleRaw,
      scale,
      bookW,
      bookH,
    });

    return {
      ok: true,
      vw,
      vh,
      panelH,
      timerH,
      safeTop,
      safeBottom,
      padL,
      padR,
      padX,
      availW,
      availH,
      scaleW,
      scaleH,
      scaleRawUnclamped,
      scaleRaw,
      scale,
      bookW,
      bookH,
    };
  };

  startWaitForFlip = (reason = 'unknown') => {
    if (this.waitForFlip) clearInterval(this.waitForFlip);

    this.waitForFlip = setInterval(() => {
      if (!this.flipRef?.current) return;

      const inst =
        this.flipRef.current.pageFlip?.() ||
        this.flipRef.current.getPageFlip?.();

      if (!inst) return;

      window.pageflipInstance = inst;

      clearInterval(this.waitForFlip);
      this.waitForFlip = null;

      console.log('[Ebook] PageFlip instance ready → window.pageflipInstance', { reason });

      try {
        const pf = this.flipRef.current?.pageFlip?.();
        const idx = typeof this.state.currentPageIdx === 'number' ? this.state.currentPageIdx : 0;
        if (pf && typeof pf.turnToPage === 'function') {
          pf.turnToPage(idx);
          console.log('[Ebook][Flip] sync -> turnToPage', idx, { reason });
        }
      } catch (e) {
        console.warn('[Ebook][Flip] sync turnToPage failed', e);
      }

      this.applyEbookScale(`flip-ready:${reason}`);
      window.removeEventListener('resize', this.applyEbookScale);
      window.addEventListener('resize', this.applyEbookScale);

      setTimeout(() => this.bindDragCaptureFix(`flip-ready:${reason}`), 0);

      setTimeout(() => this.applyEbookScale(`post-ready:${reason}`), 50);

      setTimeout(() => {
        const root =
          document.querySelector('.ebookFlipBookRoot.stf__parent') ||
          document.querySelector('.stf__parent');

        if (!root) return;

        if (this.inviteCornerRoot && this._inviteCornerEnter && this._inviteCornerLeave) {
          try {
            this.inviteCornerRoot.removeEventListener('mouseenter', this._inviteCornerEnter);
            this.inviteCornerRoot.removeEventListener('mouseleave', this._inviteCornerLeave);
          } catch (e) {}
        }

        this.inviteCornerRAF = null;
        this.inviteCornerPaused = false;

        this._inviteCornerEnter = () => {
          this.inviteCornerPaused = true;
        };

        this._inviteCornerLeave = () => {
          setTimeout(() => {
            this.inviteCornerPaused = false;
          }, 2000);
        };

        this.inviteCornerRoot = root;
        this.inviteCornerRoot.addEventListener('mouseenter', this._inviteCornerEnter);
        this.inviteCornerRoot.addEventListener('mouseleave', this._inviteCornerLeave);

        console.log('[Ebook][Invite] loop started', { reason });
      }, 2500);
    }, 250);
  };

  commitResize = (payload) => {
    if (!payload) return;

    this.setState(
      (s) => ({
        bookW: payload.bookW,
        bookH: payload.bookH,
        flipKey: s.flipKey + 1,
      }),
      () => {
        console.log('[Ebook][PageFlip][RECREATE]', {
          reason: payload.reason,
          flipKey: this.state.flipKey,
          bookW: this.state.bookW,
          bookH: this.state.bookH,
        });
      }
    );
  };

  queueResizeCommit = (payload) => {
    this._pendingResize = payload;

    if (this._resizeTO) clearTimeout(this._resizeTO);

    this._resizeTO = setTimeout(() => {
      this._resizeTO = null;
      const p = this._pendingResize;
      this._pendingResize = null;
      this.commitResize(p);
    }, this.RESIZE_THROTTLE_MS);
  };

  applyEbookScale = (reason = 'resize') => {
    const reasonStr =
      typeof reason === 'string'
        ? reason
        : `evt:${reason && reason.type ? reason.type : 'unknown'}`;

    if (this._rafScale) cancelAnimationFrame(this._rafScale);

    this._rafScale = requestAnimationFrame(() => {
      const info = this.getComputedBookSize();

      if (!info.ok) {
        console.log('[Ebook][Scale] skip', { reason: reasonStr, ...info });
        return;
      }

      const key = `${info.bookW}x${info.bookH}`;

      console.log('[Ebook][Scale]', {
        reason: reasonStr,
        key,
        vw: info.vw,
        vh: info.vh,
        panelH: info.panelH,
        timerH: info.timerH,
        safeTop: info.safeTop,
        safeBottom: info.safeBottom,
        padX: info.padX,
        availW: info.availW,
        availH: info.availH,
        scaleRaw: info.scaleRaw,
        scale: info.scale,
        nextBook: { w: info.bookW, h: info.bookH },
        currentBook: { w: this.state.bookW, h: this.state.bookH },
      });

      const dw = Math.abs(info.bookW - this.state.bookW);
      const dh = Math.abs(info.bookH - this.state.bookH);

      if (dw >= this.RESIZE_MIN_DELTA_PX || dh >= this.RESIZE_MIN_DELTA_PX) {
        this.queueResizeCommit({ bookW: info.bookW, bookH: info.bookH, reason: reasonStr });
      }

      const flipRoot = this.getFlipRoot();
      if (flipRoot) {
        const r = flipRoot.getBoundingClientRect();
        const cs = window.getComputedStyle(flipRoot);

        const item = flipRoot.querySelector('.stf__item');
        const ir = item ? item.getBoundingClientRect() : null;

        console.log('[Ebook][Scale][Rects]', {
          parentClass: flipRoot.className,
          parentRect: { w: Math.round(r.width), h: Math.round(r.height) },
          parentCSS: { zoom: cs.zoom || null, transform: cs.transform || null },
          itemRect: ir ? { w: Math.round(ir.width), h: Math.round(ir.height) } : null,
          note: 'Expect itemRect ~= bookW/bookH. If the content rect is smaller, the problem is in the EbookComponent.',
        });

        const contentEl =
          flipRoot.querySelector('[class*="EbookComponent_ebook"]') ||
          document.querySelector('.pageflip-wrapper [class*="EbookComponent_ebook"]');
        if (contentEl) {
          const cr = contentEl.getBoundingClientRect();
          console.log('[Ebook][Scale][ContentRect]', {
            contentClass: contentEl.className,
            contentRect: { w: Math.round(cr.width), h: Math.round(cr.height) },
          });
        } else {
          console.log('[Ebook][Scale][ContentRect] content not found');
        }
      } else {
        console.log('[Ebook][Scale][Rects] flipRoot not found yet', { reason: reasonStr });
      }
    });
  };

  componentDidMount() {
    document.addEventListener('startGame', this.init);

    this.startWaitForFlip('mount');

    window.removeEventListener('resize', this.applyEbookScale);
    window.addEventListener('resize', this.applyEbookScale);
  }

  componentWillUnmount() {
    clearTimeout(this.presentationTimeout);
    clearInterval(this.timerInterval);
    if (this.waitForFlip) clearInterval(this.waitForFlip);

    if (this._resizeTO) clearTimeout(this._resizeTO);
    this._resizeTO = null;
    this._pendingResize = null;

    if (this.inviteCornerRAF) cancelAnimationFrame(this.inviteCornerRAF);
    this.inviteCornerRAF = null;

    this.unbindDragCaptureFix('unmount');

    if (this.inviteCornerRoot) {
      const r = this.inviteCornerRoot.getBoundingClientRect();
      this.inviteCornerRoot.dispatchEvent(
        new MouseEvent('mousemove', {
          bubbles: true,
          cancelable: true,
          clientX: r.left + r.width * 0.5,
          clientY: r.top + r.height * 0.5,
          view: window,
        })
      );
    }

    if (this.inviteCornerRoot && this._inviteCornerEnter && this._inviteCornerLeave) {
      try {
        this.inviteCornerRoot.removeEventListener('mouseenter', this._inviteCornerEnter);
        this.inviteCornerRoot.removeEventListener('mouseleave', this._inviteCornerLeave);
      } catch (e) {}
    }

    this.inviteCornerRoot = null;
    document.removeEventListener('startGame', this.init);
    document.removeEventListener('audioEnded', this.onPresentationMediaEnded);

    window.removeEventListener('resize', this.applyEbookScale);

    console.log('[Ebook] unmount cleanup');
  }

  getFlipRoot = () =>
    document.querySelector('.ebookFlipBookRoot.stf__parent') ||
    document.querySelector('.ebookFlipBookRoot')?.closest('.stf__parent') ||
    document.querySelector('.stf__parent');

  dispatchMouseMove = (root, x, y) => {
    if (!root) return;
    const ev = new MouseEvent('mousemove', {
      bubbles: true,
      cancelable: true,
      clientX: x,
      clientY: y,
      view: window,
    });
    root.dispatchEvent(ev);
  };

  scheduleInviteSetup = (delayMs = 0, reason = '') => {
    clearTimeout(this.inviteSetupTimeout);

    this.inviteSetupTimeout = setTimeout(() => {
      const root = this.getFlipRoot();
      const pages = this.state.pages || [];
      const pageIdx = this.state.currentPageIdx || 0;
      const canFlipNext = pages.length > 1 && pageIdx < pages.length - 1;

      if (!root) {
        console.log('[Ebook][Invite] setup: root not found, retry', { reason });
        this.scheduleInviteSetup(300, 'retry-root');
        return;
      }

      this.inviteRoot = root;

      root.classList.toggle('canFlipPrev', pageIdx > 0);
      root.classList.toggle('canFlipNext', canFlipNext);

      this.bindInviteListeners(root);

      if (!this.isPointerInsideBook) {
        this.startInviteCorner(`setup:${reason}`);
      } else {
        console.log('[Ebook][Invite] setup: pointer inside → skip start', { reason });
      }
    }, delayMs);
  };

  bindInviteListeners = (root) => {
    if (!root || this.inviteListenersBound) return;

    root.addEventListener('mouseenter', this.onInviteMouseEnter, { passive: true });
    root.addEventListener('mousemove', this.onInviteMouseMove, { passive: true });
    root.addEventListener('mousedown', this.onInviteMouseDown, { passive: true });
    root.addEventListener('touchstart', this.onInviteMouseDown, { passive: true });
    root.addEventListener('mouseleave', this.onInviteMouseLeave, { passive: true });

    this.inviteListenersBound = true;
    console.log('[Ebook][Invite] listeners bound');
  };

  unbindInviteListeners = () => {
    const root = this.inviteRoot || this.getFlipRoot();
    if (!root || !this.inviteListenersBound) return;

    root.removeEventListener('mouseenter', this.onInviteMouseEnter);
    root.removeEventListener('mousemove', this.onInviteMouseMove);
    root.removeEventListener('mousedown', this.onInviteMouseDown);
    root.removeEventListener('touchstart', this.onInviteMouseDown);
    root.removeEventListener('mouseleave', this.onInviteMouseLeave);

    this.inviteListenersBound = false;
    console.log('[Ebook][Invite] listeners unbound');
  };

  scheduleInviteResume = (reason = '') => {
    clearTimeout(this.inviteResumeTimeout);

    this.inviteResumeTimeout = setTimeout(() => {
      if (this.isPointerInsideBook) {
        console.log('[Ebook][Invite] resume skip: pointer still inside', { reason });
        return;
      }
      this.startInviteCorner(`resume:${reason}`);
    }, this.INVITE_RESUME_DELAY);

    console.log('[Ebook][Invite] resume scheduled', { reason, inMs: this.INVITE_RESUME_DELAY });
  };

  startInviteCorner = (reason = '') => {
    const root = this.inviteRoot || this.getFlipRoot();
    const pages = this.state.pages || [];
    const pageIdx = this.state.currentPageIdx || 0;
    const canFlipNext = pages.length > 1 && pageIdx < pages.length - 1;

    if (!root || !canFlipNext) {
      console.log('[Ebook][Invite] start skip', {
        reason,
        hasRoot: !!root,
        pages: pages.length,
        pageIdx,
        canFlipNext,
      });
      return;
    }
    if (this.inviteActive) return;

    this.inviteActive = true;
    this.inviteStartTs = null;

    console.log('[Ebook][Invite] START', { reason });

    const rect = root.getBoundingClientRect();

    const cornerX = rect.right - 6;
    const cornerY = rect.top + 6;

    const minX = rect.left + 120;
    const maxY = rect.bottom - 120;

    const targetX = Math.max(minX, cornerX - 80);
    const targetY = Math.min(maxY, cornerY + 80);

    const centerX = rect.left + rect.width * 0.55;
    const centerY = rect.top + rect.height * 0.55;

    const bendMs = 800;
    const retMs = 600;
    const restMs = 1200;
    const cycleMs = bendMs + retMs + restMs;

    const easeInOutCubic = (t) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const lerp = (a, b, t) => a + (b - a) * t;

    const tick = (ts) => {
      if (!this.inviteActive) return;

      if (this.isPointerInsideBook) {
        this.stopInviteCorner('pointer-inside');
        return;
      }

      if (this.inviteStartTs == null) this.inviteStartTs = ts;
      const t = (ts - this.inviteStartTs) % cycleMs;

      if (t < bendMs) {
        const p = easeInOutCubic(t / bendMs);
        this.dispatchMouseMove(root, lerp(cornerX, targetX, p), lerp(cornerY, targetY, p));
      } else if (t < bendMs + retMs) {
        const p = easeInOutCubic((t - bendMs) / retMs);

        if (p < 0.78) {
          const p2 = p / 0.78;
          this.dispatchMouseMove(root, lerp(targetX, cornerX, p2), lerp(targetY, cornerY, p2));
        } else {
          const p3 = (p - 0.78) / 0.22;
          this.dispatchMouseMove(root, lerp(cornerX, centerX, p3), lerp(cornerY, centerY, p3));
        }
      } else {
        this.dispatchMouseMove(root, centerX, centerY);
      }

      this.inviteRaf = requestAnimationFrame(tick);
    };

    this.inviteRaf = requestAnimationFrame(tick);
  };

  stopInviteCorner = (reason = '') => {
    if (!this.inviteActive) return;

    this.inviteActive = false;
    this.inviteStartTs = null;

    if (this.inviteRaf) cancelAnimationFrame(this.inviteRaf);
    this.inviteRaf = null;

    const root = this.inviteRoot || this.getFlipRoot();
    if (root) {
      const r = root.getBoundingClientRect();
      this.dispatchMouseMove(root, r.left + r.width * 0.55, r.top + r.height * 0.55);
    }

    console.log('[Ebook][Invite] STOP', { reason });
  };

  onInviteMouseEnter = () => {
    this.isPointerInsideBook = true;
    this.stopInviteCorner('mouseenter');
  };

  onInviteMouseMove = () => {
    this.isPointerInsideBook = true;
    this.stopInviteCorner('mousemove');
  };

  onInviteMouseDown = () => {
    this.isPointerInsideBook = true;
    this.stopInviteCorner('mousedown/touchstart');
  };

  onInviteMouseLeave = () => {
    this.isPointerInsideBook = false;
    this.scheduleInviteResume('mouseleave');
  };

  startInviteCorner = () => {
    const ease = t => (t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t+2,3)/2);
    const state = this.inviteCorner || {};

    const dispatch = (x, y) => {
      if (!state.root) return;
      const ev = new MouseEvent('mousemove', { bubbles:true, cancelable:true, clientX:x, clientY:y, view:window });
      state.root.dispatchEvent(ev);
    };

    const loop = now => {
      if (state.stopped) return;
      if (state.paused) { state.raf = requestAnimationFrame(loop); return; }

      const r = state.root?.getBoundingClientRect();
      if (!r) { state.raf = requestAnimationFrame(loop); return; }

      const cornerX = r.right - 8, cornerY = r.top + 8;
      const cycle = state.foldMs + state.holdMs + state.returnMs + state.restMs;
      if (!state.startAt) { state.startAt = now + 400; console.log('[InviteCorner] ready'); }

      const t = now - state.startAt; if (t < 0) { state.raf = requestAnimationFrame(loop); return; }
      const tt = t % cycle;
      let p=0;
      if (tt < state.foldMs) p = ease(tt/state.foldMs);
      else if (tt < state.foldMs + state.holdMs) p = 1;
      else if (tt < state.foldMs + state.holdMs + state.returnMs) {
        const u = (tt - state.foldMs - state.holdMs)/state.returnMs;
        p = 1 - ease(u);
      } else p = 0;

      const x = cornerX - state.bendX*p;
      const y = cornerY + state.bendY*p;
      dispatch(x,y);

      state.raf = requestAnimationFrame(loop);
    };

    const onEnter = e => { if (!e.isTrusted) return; if (!state.paused){ state.paused=true; console.log('[InviteCorner] pause'); }};
    const onLeave = e => {
      if (!e.isTrusted) return;
      if (state.resumeTO) clearTimeout(state.resumeTO);
      state.resumeTO = setTimeout(()=>{ state.paused=false; console.log('[InviteCorner] resume'); }, state.resumeDelay);
    };

    const poll = setInterval(()=>{
      const root = document.querySelector('.ebookFlipBookRoot.stf__parent');
      if (!root) return;
      clearInterval(poll);
      state.root = root;
      root.addEventListener('mouseenter', onEnter);
      root.addEventListener('mouseleave', onLeave);
      state.raf = requestAnimationFrame(loop);
    },200);
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevState.flipKey !== this.state.flipKey) {
      console.log('[Ebook][PageFlip] flipKey changed -> rebind', {
        from: prevState.flipKey,
        to: this.state.flipKey,
        bookW: this.state.bookW,
        bookH: this.state.bookH,
      });
      this.startWaitForFlip('flipKey-change');
    }

    const pageChanged = prevState.currentPageIdx !== this.state.currentPageIdx;
    const modeOrTimerChanged =
      prevState.currentMode !== this.state.currentMode ||
      prevState.displayTimer !== this.state.displayTimer;

    if (pageChanged) this.onPageChange();

    if (pageChanged || modeOrTimerChanged) {
      this.applyEbookScale(pageChanged ? 'page-change' : 'mode/timer-change');
    }
  }

  init = () => {
    const {
      activity: {
        activity: { _id },
        data,
      },
    } = this.props;

    const locale = this.props.activity.activity?.monolingual
      ? ''
      : (window.location.href.indexOf('edit-activities') !== -1 &&
          this.props.activity.activity?.currentLanguageVersion?.locale
        ? this.props.activity.activity.currentLanguageVersion.locale
        : this.props?.solutionLocale?.code
        ? this.props.solutionLocale.code
        : '');

    const versionType = (!locale || locale === 'en') ? "" : `immersion_${locale}`;

    const gameData =
      versionType && data.languageVersionData?.[versionType]
        ? data.languageVersionData[versionType]
        : data.gameData;

    const { problems, totalWords, benchmark } = gameData;
    const validPages = _.filter(problems, validateProblem);

    const assetsFolder =
      versionType && data.languageVersionData?.[versionType]
        ? `${activitiesURL}/${_id}/languageVersionData/${versionType}/`
        : `${activitiesURL}/${_id}/gamedata/`;

    const hasVideo = !!validPages.filter((p) => !!p.video).length;
    const hasAudio = !!validPages.filter((p) => !!p.audio).length;

    const timerCanBeDisplayed =
      totalWords &&
      benchmark &&
      data.options.displayTimer &&
      !hasVideo &&
      data.options.ebookMode !== PRESENTATION;

    this.setState({
      hasAudio,
      hasVideo,
      videoPlaying: true,
      presentationPaused: false,
      presentationFinished: false,
      timerStarted: false,
      spentTime: 0,
      currentMode:
        data.options.ebookMode === PRESENTATION ? PRESENTATION : MANUAL,
      displayTimer: timerCanBeDisplayed,
      assetsFolder,
      currentPageIdx: 0,
      pages: validPages,
      versionType,
    }, () => {
      const pf = this.flipRef.current?.pageFlip?.();
      if (pf && typeof pf.turnToPage === 'function') {
        try {
          pf.turnToPage(0);
          console.log('[Ebook][Flip] init -> turnToPage(0)');
          const root = document.querySelector('.ebookFlipBookRoot.stf__parent');
          if (root) {
            const total = (validPages && validPages.length) ? validPages.length : 0;
            const canFlipPrev = false;
            const canFlipNext = total > 1;
            root.classList.toggle('canFlipPrev', canFlipPrev);
            root.classList.toggle('canFlipNext', canFlipNext);
            console.log('[Ebook][Peek]init', { pageIdx: 0, total, canFlipPrev, canFlipNext });
          }
        } catch (e) {
          console.warn('[Ebook][Flip] init turnToPage failed', e);
        }
      }
      console.log('[Ebook] BOOK base size:', BOOK_WIDTH, 'x', BOOK_HEIGHT);

      this.applyEbookScale('init');
    });
  };

  showPrevPage = () => {
    const pf = this.flipRef.current?.pageFlip?.();

    let idx = this.state.currentPageIdx;
    if (pf && typeof pf.getCurrentPageIndex === 'function') {
      try {
        const real = pf.getCurrentPageIndex();
        if (typeof real === 'number' && !Number.isNaN(real)) idx = real;
      } catch (e) {}
    }

    console.log('[Ebook][Prev]', { idx, stateIdx: this.state.currentPageIdx });

    if (idx <= 0) return;

    const root = this.getFlipRoot();

    if (root) {
      const r0 = root.getBoundingClientRect();
      this.dispatchMouseMove(
        root,
        Math.round(r0.left + r0.width * 0.55),
        Math.round(r0.top + r0.height * 0.55)
      );
    }

    this.unbindDragCaptureFix('prev-click');
    const restore = () => setTimeout(() => this.bindDragCaptureFix('prev-click-restore'), 0);

    if (pf && typeof pf.flipPrev === 'function') {
      requestAnimationFrame(() => {
        if (root) {
          const r = root.getBoundingClientRect();
          this.dispatchMouseMove(root, Math.round(r.left + 8), Math.round(r.top + 8));
        }

        let ret;
        try {
          ret = pf.flipPrev('top');
        } catch (e) {
          try {
            ret = pf.flipPrev();
          } catch (err) {}
        }

        console.log('[Ebook][Prev] flipPrev(animated)', { ret });

        restore();
      });
      return;
    }

    restore();
    this.setState({ currentPageIdx: idx - 1 });
  };

  showNextPage = () => {
    const {
      activity: {
        data: { options: { ebookMode } },
      },
    } = this.props;

    const { currentPageIdx } = this.state;
    const isLastPage = this.getIsLastPage();
    const pf = this.flipRef.current?.pageFlip?.();

    if (!isLastPage) {
      if (pf && typeof pf.flipNext === 'function') pf.flipNext();
      else this.setState({ currentPageIdx: currentPageIdx + 1 });
    } else if (ebookMode === PRESENTATION || ebookMode === BOTH) {
      this.setState({
        presentationPaused: true,
        presentationFinished: true,
        videoPlaying: false,
      });
    }
  };

  getIsLastPage = () => {
    const { currentPageIdx, pages } = this.state;
    return pages.length === currentPageIdx + 1;
  };

  onPageChange = () => {
    const { autoPlayAudioInManual } = this.props.activity.data.options;
    const { currentMode, hasVideo, displayTimer } = this.state;

    const isLastPage = this.getIsLastPage();
    if (isLastPage && !hasVideo && !displayTimer)
      this.props.setActivityMastered();

    this.toggleVideo(true);
    clearTimeout(this.presentationTimeout);
    stopAudio();

    if (currentMode === PRESENTATION) this.playMediaAndSetupInterval();
    else if (autoPlayAudioInManual) this.playPageAudio();
  };

  getCurrentPage = () => {
    const { pages, currentPageIdx } = this.state;
    return pages ? pages[currentPageIdx] : null;
  };

  playPageAudio = () => {
    const { assetsFolder } = this.state;
    const currentPage = this.getCurrentPage();
    if (!currentPage?.audio) return;
    toggleAudio(assetsFolder + currentPage.audio, { onEnd: this.onAudioEnded });
  };

  startPresentation = () => {
    const { presentationFinished } = this.state;
    if (presentationFinished) {
      this.setState(
        {
          videoPlaying: true,
          currentMode: PRESENTATION,
          currentPageIdx: 0,
          presentationPaused: false,
          presentationFinished: false,
        },
        () => {
          const pf = this.flipRef.current?.pageFlip?.();
          if (pf && typeof pf.turnToPage === 'function') pf.turnToPage(0);
          this.playMediaAndSetupInterval();
        }
      );
    } else {
      this.setState(
        { videoPlaying: true, currentMode: PRESENTATION, presentationPaused: false },
        this.playMediaAndSetupInterval
      );
    }
  };

  stopPresentaion = () => {
    const {
      activity: { data: { options: { ebookMode } } },
    } = this.props;

    stopAudio();
    clearTimeout(this.presentationTimeout);
    this.setState({ videoPlaying: false });
    document.removeEventListener('audioEnded', this.onPresentationMediaEnded);

    if (ebookMode === BOTH) this.setState({ currentMode: MANUAL });
    else if (ebookMode === PRESENTATION) this.setState({ presentationPaused: true });
  };

  onAudioEnded = () => {
    const { displayTimer, hasVideo, hasAudio } = this.state;
    if (!displayTimer && hasVideo && hasAudio) this.props.setActivityMastered();
  };

  onVideoEnded = () => {
    const { currentMode, presentationPaused, displayTimer, hasAudio } = this.state;
    this.setState({ videoPlaying: false });
    const currentPage = this.getCurrentPage();
    if (currentMode === PRESENTATION && !presentationPaused && !currentPage.audio)
      this.onPresentationMediaEnded();
    if (!displayTimer && !hasAudio) this.props.setActivityMastered();
  };

  toggleVideo = (state) => this.setState({ videoPlaying: state });

  onFlipStateChange = (e) => {
    const st = e && e.data ? String(e.data) : '';
    const isFlipping = st === 'flipping' || st === 'fold_corner' || st === 'user_fold';

    if (isFlipping !== this.state.isFlipping) {
      console.log('[Ebook][Flip] state:', st, '=> isFlipping:', isFlipping);
      this.setState({ isFlipping });
    }
  };

  onParentAdviceToggle = (isOpening) => isOpening && this.stopPresentaion();

  playMediaAndSetupInterval = () => {
    const currentPage = this.getCurrentPage();
    const { audio, video } = currentPage;
    clearInterval(this.presentationTimeout);
    document.removeEventListener('audioEnded', this.onPresentationMediaEnded);
    if (audio) {
      this.playPageAudio();
      document.addEventListener('audioEnded', this.onPresentationMediaEnded);
    }
    if (!audio && !video)
      this.presentationTimeout = setTimeout(this.showNextPage, NO_MEDIA_DELAY);
  };

  onPresentationMediaEnded = (e) => {
    const delay = e && e.detail.error ? NO_MEDIA_DELAY : WITH_MEDIA_DELAY;
    this.toggleVideo(false);
    this.presentationTimeout = setTimeout(this.showNextPage, delay);
  };

  startTimer = () => {
    const { displayTimer } = this.state;
    if (!displayTimer) return;
    this.setState({ timerStarted: true });
    this.timerInterval = setInterval(this.increaseTimer, 1000);
  };

  stopTimer = () => {
    const { timerStarted } = this.state;
    if (!timerStarted) return;
    clearInterval(this.timerInterval);
    this.setState({ timerStarted: false }, this.showResults);
  };

  increaseTimer = () => this.setState((s) => ({ spentTime: s.spentTime + 1 }));

  showResults = () => {
    const { showStats } = this.props;
    const { spentTime, displayTimer, versionType } = this.state;
    if (!displayTimer) return;
    let gameData = versionType
      ? this.props.activity.data.languageVersionData?.[versionType]
      : null;
    if (!gameData?.problems || !gameData?.totalWords)
      gameData = this.props.activity.data.gameData;
    const { totalWords, benchmark } = gameData;
    const speed = Math.round((totalWords / spentTime) * 60);
    showStats({
      data: { speed, benchmark },
      ebookBenchmark: { benchmark, totalWords, speed, spentTime },
    });
  };

  render() {
    const {
      displayTimer,
      assetsFolder,
      currentPageIdx,
      pages,
      videoPlaying,
      spentTime,
      timerStarted,
      currentMode,
      presentationPaused,
      flipKey,
      bookW,
      bookH,
    } = this.state;

    const {
      activity: {
        data: { options: { ebookMode } },
      },
      isPlayVersion,
    } = this.props;

    const currentPage = this.getCurrentPage();
    const { audio } = currentPage || {};
    const isLastPage = pages && pages.length === currentPageIdx + 1;

    console.log('Ebook versionType: ', this.state.versionType);
    console.log('Ebook isPlayVersion: ', isPlayVersion);
    console.log('Ebook solutionLocale: ', this.props.solutionLocale);

    const startPage = Math.min(
      Math.max(typeof currentPageIdx === 'number' ? currentPageIdx : 0, 0),
      (pages && pages.length ? pages.length : 1) - 1
    );

    return (
      <div className='pageflip-wrapper'>
        <style>{`
  .pageflip-wrapper {
    width: 100vw;
    height: 100vh;
    position: relative;
    overflow: hidden;
  }

  .pageflip-wrapper .ebookFlipBookOuter {
    position: absolute;
    left: 0;
    right: 0;
    top: var(--ebookSafeTop, 96px);
    bottom: var(--ebookSafeBottom, 140px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 8px;
    box-sizing: border-box;
    z-index: 10;
  }

  .pageflip-wrapper .globa-button-pagefliparea {
    position: absolute;
    left: 50%;
    bottom: 0px;
    transform: translateX(-50%);
    z-index: 1001;
    bottom: env(safe-area-inset-bottom, 0px);
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }

  .EbookComponent_ebookWrapper__3jIvE {
    top: 55%;
  }

  .pageflip-wrapper .ebookFlipBookRoot.stf__parent,
  .pageflip-wrapper .stf__parent {
    transform: none !important;
    zoom: 1 !important;
    transform-origin: top left;
    touch-action: none;
    -ms-touch-action: none;
    position: relative;
  }

  .pageflip-wrapper .ebookPage {
    width: 100% !important;
    height: 100% !important;
    box-sizing: border-box !important;
  }

  .pageflip-wrapper .stf__page,
  .pageflip-wrapper .stf__page > div {
    width: 100% !important;
    height: 100% !important;
    box-sizing: border-box !important;
  }

  .pageflip-wrapper .ebookPage > div { width: 100% !important; height: 100% !important; }

  .pageflip-wrapper .ebookPage [class*="EbookComponent_ebook"] {
    width: 100% !important;
    height: 100% !important;
    max-width: none !important;
    max-height: none !important;
    margin: 0 !important;
  }

  .pageflip-wrapper .ebookPage [class*="EbookComponent_ebookWrapper"] {
    width: 100% !important;
    height: 100% !important;
    max-width: none !important;
    max-height: none !important;
    left: 0 !important;
    right: 0 !important;
    top: 0 !important;
    bottom: 0 !important;
    transform: none !important;
  }

  @media print {
    .globa-button-pagefliparea,
    [class*="EbookTimer"],
    [class*="Timer"],
    [class*="Buttons_outerClass__"],
    [class*="EbookComponent_navBtn"],
    [class*="EbookComponent_prevBtn"],
    [class*="EbookComponent_nextBtn"] {
      display: none !important;
      visibility: hidden !important;
    }
  }
`}</style>


        {displayTimer && currentMode === MANUAL && (
          <AdaptiveContainer
            style={{
              position: 'absolute',
              left: '50%',
              top: '1%',
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '8px',
              transform: 'translateX(-50%)',
              zIndex: 1002,
            }}
          >
            <EbookTimer
              showStart={currentPageIdx === 0 && !timerStarted}
              startHandler={this.startTimer}
              showStop={isLastPage && timerStarted}
              stopHandler={this.stopTimer}
              spentTime={spentTime}
            />
          </AdaptiveContainer>
        )}

        {pages && pages.length > 0 && (
          <div className="ebookFlipBookOuter">
            <HTMLFlipBook
              ref={this.flipRef}
              className="ebookFlipBookRoot"
              key={`flip-${flipKey}-${bookW}x${bookH}`}
              startPage={startPage}
              width={bookW}
              height={bookH}
              size="fixed"
              autoSize={false}
              showCover={false}
              usePortrait={true}
              flippingTime={FLIP_TIME}
              drawShadow={true}
              maxShadowOpacity={0.35}
              mobileScrollSupport={false}
              useMouseEvents={true}
              disableFlipByClick={false}
              clickEventForward={true}
              showPageCorners={true}
              swipeDistance={10}
              onFlip={(e) => {
                const raw = e ? e.data : null;

                let nextIdx = null;
                if (typeof raw === 'number') nextIdx = raw;
                else if (typeof raw === 'string' && raw.trim() !== '' && !Number.isNaN(Number(raw))) nextIdx = Number(raw);
                else if (raw && typeof raw === 'object') {
                  if (typeof raw.page === 'number') nextIdx = raw.page;
                  else if (typeof raw.index === 'number') nextIdx = raw.index;
                }

                if (typeof nextIdx !== 'number') {
                  try {
                    const pf = this.flipRef.current?.pageFlip?.();
                    if (pf && typeof pf.getCurrentPageIndex === 'function') {
                      nextIdx = pf.getCurrentPageIndex();
                    }
                  } catch (err) {}
                }

                if (typeof nextIdx === 'number' && nextIdx !== this.state.currentPageIdx) {
                  console.log('[Ebook][Flip] onFlip -> pageIdx:', nextIdx);

                  const root = document.querySelector('.ebookFlipBookRoot.stf__parent');
                  if (root) {
                    const total = (pages && pages.length) ? pages.length : 0;

                    const canFlipPrev = nextIdx > 0;
                    const canFlipNext = total ? nextIdx < (total - 1) : true;

                    root.classList.toggle('canFlipPrev', canFlipPrev);
                    root.classList.toggle('canFlipNext', canFlipNext);

                    console.log('[Ebook][Peek]', { pageIdx: nextIdx, total, canFlipPrev, canFlipNext });
                  }

                  this.setState({ currentPageIdx: nextIdx });
                }
              }}
            >
              {pages.map((page, idx) => {
                const isActive = idx === currentPageIdx;
                const allowNav = isActive && currentMode !== PRESENTATION;

                return (
                  <div key={idx} className="ebookPage" style={{ width: '100%', height: '100%' }}>
                    <EbookComponent
                      videoPlaying={isActive ? videoPlaying : false}
                      showVideReplayBtn={!videoPlaying && currentMode !== PRESENTATION && isActive}
                      replayHandler={this.toggleVideo.bind(this, true)}
                      onVideoEnded={this.onVideoEnded}
                      showPrev={allowNav && currentPageIdx !== 0}
                      showNext={allowNav && currentPageIdx !== pages.length - 1}
                      prevClickHandler={this.showPrevPage}
                      nextClickHandler={this.showNextPage}
                      page={page}
                      assetsFolder={assetsFolder}
                    />
                  </div>
                );
              })}
            </HTMLFlipBook>
          </div>
        )}

        <div className="globa-button-pagefliparea">
          <ActivityButtons
            buttons={[
              { type: GO_BACK, onClick: isLastPage && timerStarted ? this.stopTimer : undefined },
              {
                type: GO,
                dontShow:
                  ebookMode === MANUAL ||
                  (currentMode === PRESENTATION && !presentationPaused) ||
                  !currentPage ||
                  timerStarted,
                onClick: this.startPresentation,
              },
              {
                type: STOP,
                dontShow:
                  ebookMode === MANUAL ||
                  (currentMode === PRESENTATION && presentationPaused) ||
                  !currentPage ||
                  timerStarted ||
                  currentMode === MANUAL,
                onClick: this.stopPresentaion,
              },
              HELP,
              { type: SPEAK, dontShow: !audio || currentMode === PRESENTATION, onClick: this.playPageAudio },
              { type: PARENT_ADVICE, preClick: this.onParentAdviceToggle },
            ]}
          />
        </div>
      </div>
    );
  }
}

Ebook.propTypes = {
  showStats: PropTypes.func.isRequired,
  setActivityMastered: PropTypes.func.isRequired,
};

export default connect(null, { showStats, setActivityMastered })(Ebook);
